import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack} from 'views/components';
import {translate} from '../../Translate';

import WeeklyCalendarUsersHeader from './row/WeeklyCalendarUsersHeader';
import WeeklyCalendarUsersRow from './row/WeeklyCalendarUsersRow';
import WeeklyCalendarTimeChanger from './helpers/WeeklyCalendarTimeChanger';
import WeeklyCalendarFilter from './helpers/WeeklyCalendarFilter';
import LoadingScreen from './skeleton/LoadingScreen';

const WeeklyCalendarUsers = (props) => {
  const {
    users,
    loadUserSchedules,
    getUserRn,
    headerContent,
    UserRow,
    EventTile,
    loadingUsers,
    userFilter,
    eventFilter,
  } = props;

  const [weekDays, setWeekDays] = React.useState([]);
  const [scheduleEvents, setScheduleEvents] = React.useState({});
  const [filterSelection, setFilterSelection] = React.useState({filter: {}});

  const handleWeekChange = (newWeekDays) => {
    setWeekDays(newWeekDays);
  };

  const filterOptions = React.useMemo(() => {
    const filterOptions = {};

    let rank = 0;
    const userOptions = {};
    for (const filter of userFilter) {
      userOptions[filter.key] = {
        label: filter.label,
        rank: ++rank,
        key: filter.key,
        filter: filter.filter,
        defaultEnabled: filter.defaultEnabled,
      };
    }

    filterOptions['user'] = {
      key: 'user',
      label: translate('weeklyScheduleCalendar.Filter.User'),
      rank: 0,
      options: userOptions,
    };

    const eventOptions = {};
    for (const filter of eventFilter) {
      eventOptions[filter.key] = {
        label: filter.label,
        rank: ++rank,
        key: filter.key,
        filter: filter.filter,
        defaultEnabled: filter.defaultEnabled,
      };
    }

    filterOptions['event'] = {
      key: 'event',
      label: translate('weeklyScheduleCalendar.Filter.Event'),
      rank: 0,
      options: eventOptions,
    };


    return filterOptions;
  }, [userFilter, eventFilter]);

  React.useEffect(() => {
    for (const filter of Object.values(filterOptions)) {
      for (const options of Object.values(filter.options)) {
        if (options.defaultEnabled) {
          updateFilter(filter, options);
        }
      }
    }
  }, [filterOptions]);

  const resetFilter = () => {
    setFilterSelection({filter: {}});
  };

  const updateFilter = (filter, option) => {
    setFilterSelection((e) => {
      if (option) {
        return {
          ...e,
          filter: {
            ...e.filter,
            [filter.key]: {
              filterData: filter,
              optionSelected: option.key,
              optionSelectedData: filter.options[option.key],
            },
          },
        };
      } else {
        const newFilter = {...e.filter};
        delete newFilter[filter.key];
        return {
          ...e,
          filter: newFilter,
        };
      }
    });
  };

  React.useEffect(() => {
    const fetchEvents = async () => {
      setScheduleEvents({});

      for (let i = 0; i < users.length; i += 5) {
        const batch = users.slice(i, i + 5);
        const eventsArray = await Promise.all(
            batch.map((user) => loadUserSchedules(user, weekDays[0], weekDays[6])),
        );
        setScheduleEvents((prev) => {
          const updated = {...prev};
          batch.forEach((user, idx) => {
            updated[getUserRn(user)] = eventsArray[idx];
          });
          return updated;
        });
      }
    };
    fetchEvents();
  }, [users, weekDays, loadUserSchedules, getUserRn]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{px: 2, py: 1}}
      >
        <Box>
          <WeeklyCalendarFilter
            selection={filterSelection}
            filterOptions={filterOptions}
            updateFilter={updateFilter}
            onClearFilter={resetFilter}
          />
        </Box>
        <Box>
          <WeeklyCalendarTimeChanger
            onWeekChange={handleWeekChange}
            filterOptions={{}}
          />
        </Box>
      </Stack>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
      >
        <WeeklyCalendarUsersHeader
          weekDays={weekDays}
          headerContent={headerContent}
        />
      </Box>
      {
        loadingUsers &&
        <LoadingScreen
          weekDays={weekDays}
        />
      }
      {
        !loadingUsers &&
        users.map((user) => (
          <WeeklyCalendarUsersRow
            key={getUserRn(user)}
            user={user}
            weekDays={weekDays}
            scheduleEvents={scheduleEvents[getUserRn(user)]}
            UserRow={UserRow}
            EventTile={EventTile}
            filterSelection={filterSelection}
          />
        ))
      }
    </Box>
  );
};

WeeklyCalendarUsers.propTypes = {
  headerContent: PropTypes.node.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadUserSchedules: PropTypes.func.isRequired,
  getUserRn: PropTypes.func.isRequired,
  UserRow: PropTypes.elementType.isRequired,
  EventTile: PropTypes.elementType.isRequired,
  loadingUsers: PropTypes.bool.isRequired,
  userFilter: PropTypes.array.isRequired,
  eventFilter: PropTypes.array.isRequired,
};
export default React.memo(WeeklyCalendarUsers);
